<script setup>
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import AsistenciasHoyCard from "./components/AsistenciasHoyCard.vue";
import api from '@/api';
import { ref, onMounted } from "vue";
import { useRouter } from 'vue-router'
import { encodeId } from '@/utils/obfuscate'
const router = useRouter()

const sociosActivos = ref(0);
const membresiasActivas = ref(0);
const membresiasPorVencer = ref(0);
const pagosRecientes = ref(0);

const porVencerPreview = ref([]);
const loadingPreview = ref(false);

const cargarDatosDashboard = async () => {
  try {
    const { data } = await api.get("/dashboard/contador");
    sociosActivos.value = data.sociosActivos;
    membresiasActivas.value = data.membresiasActivas;
    membresiasPorVencer.value = data.membresiasPorVencer;
    pagosRecientes.value = data.totalPagosRecientes;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.error("Error cargando dashboard:", e);
  }
};

const cargarPorVencerPreview = async () => {
  try {
    loadingPreview.value = true;
    const { data } = await api.get('/dashboard/dashboard/membresias', {
      params: { scope: 'por-vencer', days: 7, page: 1, limit: 5 }
    });
    porVencerPreview.value = data.items || [];
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.error("Error preview por vencer:", e);
  } finally {
    loadingPreview.value = false;
  }
};

function go(scope) {
  router.push({ name: 'DashboardMembresias', query: { scope } })
}
function goSocio(id) {
  router.push({ name: 'SocioDetalle', params: { id: encodeId(id) } })
}

onMounted(() => {
  cargarDatosDashboard();
  cargarPorVencerPreview();
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-lg-3 col-md-6 col-12">
        <mini-statistics-card title="Socios Registrados" :value="sociosActivos.toLocaleString()" description=""
          :icon="{
            component: 'ni ni-money-coins',
            background: 'bg-gradient-primary',
            shape: 'rounded-circle',
          }" />
      </div>
      <div class="col-lg-3 col-md-6 col-12">
        <div role="button" @click="go('activas')" class="card-click">
          <mini-statistics-card title="Membresias Activas" :value="membresiasActivas.toLocaleString()"
            :icon="{ component: 'ni ni-world', background: 'bg-gradient-danger', shape: 'rounded-circle' }" />
        </div>
      </div>
      <div class="col-lg-3 col-md-6 col-12">
        <div role="button" @click="go('por-vencer')" class="card-click">
          <mini-statistics-card title="Membresias por vencer (7d)" :value="`+${membresiasPorVencer.toLocaleString()}`"
            :icon="{ component: 'ni ni-paper-diploma', background: 'bg-gradient-success', shape: 'rounded-circle' }" />
        </div>
      </div>
      <div class="col-lg-3 col-md-6 col-12">
        <mini-statistics-card title="Pagos Recientes" :value="`$${Number(pagosRecientes).toLocaleString()}`"
          description="" :icon="{
            component: 'ni ni-cart',
            background: 'bg-gradient-warning',
            shape: 'rounded-circle',
          }" />
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-lg-5 mb-4">
        <asistencias-hoy-card />
      </div>
      <div class="col-lg-7 mb-4">
        <div class="card h-100">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h6 class="mb-0">Membresías por vencer</h6>
              <p class="text-sm text-muted mb-0">Próximos 7 días — top 5</p>
            </div>
            <button class="btn btn-sm btn-outline-primary mb-0" @click="go('por-vencer')">Ver todo</button>
          </div>
          <div class="card-body p-3">
            <div v-if="loadingPreview" class="text-sm text-muted">Cargando…</div>
            <div v-else-if="porVencerPreview.length === 0" class="text-sm text-muted">Sin vencimientos próximos.</div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th class="text-xs">Socio</th>
                    <th class="text-xs">Vence</th>
                    <th class="text-xs">Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in porVencerPreview" :key="r.membresia_usuario_id || r.usuario_id">
                    <td class="text-sm">
                      <div class="d-flex align-items-center gap-2">
                        <div class="avatar-sm">
                          <img v-if="r.avatar_url" :src="r.avatar_url" class="avatar-img" alt="" />
                          <div v-else class="avatar-fallback-sm">{{ (r.socio || '?').charAt(0) }}</div>
                        </div>
                        <div>
                          <div class="fw-semibold text-sm">{{ r.socio }}</div>
                          <div class="text-xs text-muted">{{ r.identificacion || '' }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="text-xs">{{ r.fecha_fin }}</td>
                    <td><span class="badge bg-warning text-dark text-xxs">{{ r.estado }}</span></td>
                    <td class="text-end">
                      <button class="btn btn-xs btn-outline-secondary" @click="goSocio(r.usuario_id)">Ver</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.card-click {
  cursor: pointer;
}
.card-click :deep(.card) {
  transition: transform .12s ease, box-shadow .12s ease;
}
.card-click:hover :deep(.card) {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, .1);
}
.avatar-sm {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f2f5;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.avatar-img { width: 100%; height: 100%; object-fit: cover; }
.avatar-fallback-sm { font-size: 0.7rem; font-weight: 700; color: #445; }
.text-xxs { font-size: 0.65rem; }
.btn-xs { padding: 0.15rem 0.4rem; font-size: 0.7rem; }
</style>
